import React, { useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { SITE_IMAGE_SLOTS } from "@/lib/content";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { Upload, Loader2, RotateCcw, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import useAdminLoad, { payloadShapeError } from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";

export default function SiteImagesAdmin() {
  const { lang } = useLanguage();
  const t = useT("admin");
  // The `common` binding extractApiError needs to translate the refusals it knows by code
  // (TENANT_SUSPENDED) and Pydantic's English prose inside a 422 — see lib/apiErrors.js.
  const tv = useT("common");
  const [overrides, setOverrides] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/content");
    if (!Array.isArray(data)) throw payloadShapeError();
    const map = {};
    data.forEach((b) => { map[b.key] = b.value_fr || b.value_en || ""; });
    setOverrides(map);
  }, []);
  // BUG-051 — a failed read is NOT a site running on the stock images. With `overrides`
  // stuck at `{}`, every slot showed its BUNDLED fallback, badged "Default", beside a
  // "Change" button: the practitioner's own live logo and hero were invisible, and the
  // obvious reaction is to upload them again. The grid is therefore withheld until a read
  // succeeds — a slot cannot say which image is live if nobody read the answer.
  const { loadError, reload } = useAdminLoad(load);

  const fullUrl = (path) => (path?.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusyKey(slot.key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/content", { key: slot.key, value_fr: data.url, value_en: data.url });
      toast.success(t("siteImages.toast.uploaded"));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("siteImages.toast.failed"), tv));
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (slot) => {
    setBusyKey(slot.key);
    try {
      // A 404 means the override is ALREADY gone, which is exactly what "reset to the
      // default image" asks for — so it is not a failure. Everything else is: the previous
      // `.catch(() => {})` announced "Image reset" for a delete that was refused (a 403 on
      // a suspended practice, a 500), leaving the custom image on the live site.
      await api.delete(`/admin/content/${encodeURIComponent(slot.key)}`)
        .catch((e) => { if (e?.response?.status !== 404) throw e; });
      toast.success(t("siteImages.toast.reset"));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("siteImages.toast.failed"), tv));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid="admin-site-images">
      <h1 className="font-display text-4xl text-eh-ink flex items-center gap-3">
        <ImageIcon size={28} className="text-eh-sage-deep" /> {t("nav.siteImages")}
      </h1>
      <p className="text-eh-muted mt-2 max-w-2xl">{t("siteImages.subtitle")}</p>

      <LoadFailure
        message={loadError ? extractApiError(loadError, tv("errors.loadFailed"), tv) : ""}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="admin-site-images-load-error"
        retryTestId="admin-site-images-retry"
        className="mt-8"
      />

      {!loadError && (
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SITE_IMAGE_SLOTS.map((slot) => {
          const current = overrides[slot.key];
          const isCustom = !!current;
          const src = fullUrl(current || slot.fallback);
          const busy = busyKey === slot.key;
          return (
            <div key={slot.key} data-testid={`site-image-${slot.key}`} className="bg-white border border-eh-border-soft rounded-2xl overflow-hidden">
              <div className="aspect-[4/3] bg-eh-cream flex items-center justify-center overflow-hidden">
                <img src={src} alt={lang === "fr" ? slot.labelFr : slot.labelEn} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-eh-ink text-sm">{lang === "fr" ? slot.labelFr : slot.labelEn}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${isCustom ? "bg-eh-sage/20 text-eh-sage-deep" : "bg-eh-bg text-eh-muted"}`}>
                    {isCustom ? t("siteImages.custom") : t("siteImages.default")}
                  </span>
                </div>
                <div className="text-xs text-eh-muted mt-1">{lang === "fr" ? slot.hintFr : slot.hintEn}</div>
                <div className="mt-3 flex items-center gap-2">
                  <label className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-eh-ink text-eh-bg rounded-full px-4 py-2 cursor-pointer hover:opacity-90 transition" data-testid={`site-image-change-${slot.key}`}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("siteImages.change")}
                    <input
                      type="file"
                      accept="image/*"
                      disabled={busy}
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && upload(slot, e.target.files[0])}
                    />
                  </label>
                  {isCustom && (
                    <button
                      onClick={() => reset(slot)}
                      disabled={busy}
                      data-testid={`site-image-reset-${slot.key}`}
                      title={t("siteImages.reset")}
                      className="p-2 rounded-full border border-eh-border-soft text-eh-muted hover:text-eh-ink hover:bg-eh-bg transition"
                    >
                      <RotateCcw size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
